// filepath: apps/web/src/features/lists/tree/hooks/useTreeRecords.ts
//
// FR-UI-VIEW-2 AC-1 / AC-2 / AC-3 / AC-8 — TanStack Query hook for
// the Tree view's record fetch.
//
// The Tree renderer issues GET /api/entity-records/{entityType}
// against the same FR-QRY-1 endpoint the EntityListTable uses
// (apps/web/src/features/lists/hooks/useEntityList.ts). The cache
// key shape differs intentionally so the Tree window cannot
// accidentally mutate the list-view cache (and vice versa):
//
//   list  : ["list", tenantSlug, entityType, filterHash, sortKey, cursor]
//   kanban: ["lists", tenantSlug, "kanban", entityType, viewConfigVersion]
//   tree  : ["lists", tenantSlug, "tree", entityType, viewConfigVersion, parentField]
//
// The Tree window ignores URL-derived filter/sort/cursor state — the
// table is the URL-driven surface. The Tree window fetches ALL
// records (subject to the 500-record cap) — no per-parent refetch.
// The server still applies CanRead field stripping (INV-2 / FR-AUTH-7):
// the renderer iterates `Object.keys(record.fields)` and renders
// exactly what the server returned. The parent_field value is
// resolved by `normaliseTreeRecord`; an unknown id is classified
// as "orphan root" (AC-2 / AC-4) at tree-build time.
//
// SECURITY / INTEGRITY INVARIANTS:
//   - INV-1: tenantSlug is the 2nd segment of the cache key tuple.
//   - INV-2: the hook does NOT do per-row fetches to "fill in"
//     server-stripped fields. The wire shape is authoritative.
//   - INV-7: records without a usable row_version are dropped
//     before they reach the renderer (no verifiable OCC token).
//   - FR-UI-VIEW-2 AC-3: the `parent_field` argument influences the
//     cache key so a definition re-publication that rebinds parent
//     to a different field name automatically invalidates the
//     previous cache entry.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type { ListResponseWire } from "../../types";
import { normaliseTreeRecord, type TreeRecord } from "../types";

/**
 * Hard cap the server enforces on the Tree window. Mirrors the
 * "500-of-600" rendering rule at FR-UI-VIEW-2 AC-8 — the cap is
 * server-enforced so the SPA does not need to second-guess the
 * result size. Surfaced as a constant for tests + the
 * TreeCountBanner so a future FR bumping the cap has a single
 * source of truth.
 *
 * Mirrors the `MaxTreeDepthCap = 64` constant on the server side
 * at internal/entity/view_config_validators.go (different
 * dimension — this cap counts rows, server counts depth).
 */
export const TREE_RECORDS_CAP = 500;

/**
 * TanStack Query cache key factory for the Tree window.
 *
 * Shape: `["lists", tenantSlug, "tree", entityType, viewConfigVersion, parentField]`.
 *
 * - 1st segment `"lists"` is the feature namespace so other
 *   list-feature hooks (useEntityList, useKanbanRecords) do not
 *   collide.
 * - 2nd segment `tenantSlug` is the INV-1 invariant — switching
 *   tenants resolves to a distinct cache entry.
 * - 3rd segment `"tree"` disambiguates the Tree window from the
 *   Kanban window (`"kanban"`) and any future list-feature view
 *   (gantt — `"gantt"`).
 * - 4th segment `entityType` is the entity-type filter.
 * - 5th segment `viewConfigVersion` is the entity definition's
 *   `version` number — when the definition is re-published the
 *   cache entry is invalidated automatically.
 * - 6th segment `parentField` is the resolved `parent_field` name
 *   — re-binding the parent to a different field name
 *   automatically invalidates the previous cache entry.
 */
export const treeKeys = {
  all: ["lists"] as const,
  window: (
    tenantSlug: string,
    entityType: string,
    viewConfigVersion: number,
    parentField: string,
  ) =>
    [
      "lists",
      tenantSlug,
      "tree",
      entityType,
      String(viewConfigVersion),
      parentField,
    ] as const,
};

export interface UseTreeRecordsOptions {
  /** Target entity type id. Required. */
  entityType: string;
  /**
   * The entity-definition version that supplied the `view_config`
   * block. When this changes the cache key changes automatically,
   * so a definition re-publication refreshes the Tree view.
   */
  viewConfigVersion: number;
  /**
   * The resolved `tree.parent_field` name. Part of the cache key
   * so re-binding to a different parent invalidates the cached
   * result.
   */
  parentField: string;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
  /**
   * When false, the query is disabled (no fetch). Used when the
   * definition is still loading or the parentField is missing.
   */
  enabled?: boolean;
}

export interface TreeEnvelope {
  /**
   * Normalised records the renderer should display. Each record is
   * projected into `TreeRecord` (id + parentValue + rowVersion +
   * server-stripped fields). Records missing a usable rowVersion
   * (INV-7) are dropped during normalisation.
   */
  records: TreeRecord[];
  /**
   * Server-supplied total record count. Null when the server wire
   * omits the additive `total` field (older deployments). When
   * null, the TreeCountBanner stays off.
   */
  total: number | null;
}

export interface UseTreeRecordsResult {
  /** Tree envelope. `undefined` while loading or before first success. */
  data: TreeEnvelope | undefined;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** True while any fetch (including background refetch) is in flight. */
  isFetching: boolean;
  /** Last error, classified as an `ApiError` on non-2xx responses. */
  error: ApiError | null;
  /** Force refetch (FR-UI-CORE-10 retry). */
  refetch: () => Promise<unknown>;
}

/**
 * Normalise a ListResponseWire into the Tree-friendly shape.
 *
 * Reads the additive `total` field when present (FR-QRY-2 wire
 * shape extension for FR-UI-VIEW-1 / FR-UI-VIEW-2 banner support).
 * When absent, `total` is null and the TreeCountBanner stays off.
 *
 * The server still applies CanRead field stripping on every
 * record (INV-2 / FR-AUTH-7) — the renderer MUST iterate only
 * `Object.keys(record.fields)` and never fall back to a per-row
 * GET to "fill in" absent fields.
 */
function normaliseTreePage(
  wire: ListResponseWire,
  parentField: string,
): TreeEnvelope {
  const records: TreeRecord[] = (wire.records ?? [])
    .map((r) => normaliseTreeRecord(r, parentField))
    .filter((rec): rec is TreeRecord => rec !== null);
  const total = readTotal(wire);
  return { records, total };
}

/**
 * Read the optional `total` field from a ListResponse envelope.
 *
 * Mirrors useKanbanRecords.readTotal — the FR-QRY-2 forward-compat
 * shape adds `total_count`; the FR-UI-VIEW-1 / FR-UI-VIEW-2
 * additive shape carries `total`. We accept both.
 */
function readTotal(wire: ListResponseWire): number | null {
  const raw = (wire as unknown as { total?: unknown }).total;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && /^[0-9]+$/.test(raw)) {
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * useTreeRecords — TanStack Query hook backing the Tree window.
 *
 * The hook is "always-enabled" once the parent has a non-empty
 * `entityType`, `parentField`, and `viewConfigVersion`. Passing
 * `enabled: false` suppresses the fetch (used when the Tree
 * window should not be drawn at all — e.g. while the parent's
 * loading skeleton is visible, or before the parentField resolves).
 */
export function useTreeRecords(
  opts: UseTreeRecordsOptions,
): UseTreeRecordsResult {
  const {
    entityType,
    viewConfigVersion,
    parentField,
    apiBase = "/api",
    enabled = true,
  } = opts;

  const { tenantSlug } = useTenant();

  const queryKey = treeKeys.window(
    tenantSlug,
    entityType,
    viewConfigVersion,
    parentField,
  );

  const query: UseQueryResult<TreeEnvelope, ApiError> = useQuery<
    TreeEnvelope,
    ApiError
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      // Defensive: even though `enabled` gates the query, we double-
      // check here so the test harness (which can mock `enabled`
      // off but still trigger the function) never crashes.
      if (!parentField) {
        throw new ApiError("tree.parent_field is empty", 0, "invalid_request");
      }
      const params = new URLSearchParams();
      // Tree cap is server-enforced; we hint to the server that
      // we want the cap applied via the standard `limit` param.
      // The SPA never requests more than TREE_RECORDS_CAP on its
      // own (limits the blast radius of a misconfigured tenant).
      params.set("limit", String(TREE_RECORDS_CAP));
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}?${params.toString()}`;

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // AbortSignal is forwarded via `init.signal` so TanStack
        // Query's unmount-cancellation reaches the underlying
        // fetch.
        response = await apiFetch(url, {
          method: "GET",
          init: {
            headers: {
              Accept: "application/json",
            },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const data = (await response.json()) as ListResponseWire;
      return normaliseTreePage(data, parentField);
    },
    enabled: enabled && entityType.length > 0 && parentField.length > 0,
    // FR-UI-VIEW-2 AC-6 — the tree is read-only at this stage; no
    // mutation surface. staleTime tracks the kanban hook so a
    // refetched record count picks up concurrent edits within a
    // few seconds.
    staleTime: 5_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: async () => {
      await query.refetch();
    },
  };
}

// ----------------- tiny helpers (local — not exported) ---------------

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  return null;
}
